import { v4 as uuidv4 } from 'uuid';
import type { ChatStreamDeps } from '../services/chatStream/chatStreamClient';
import { getLectureTree } from '../services/lectures';
import { store, type RootState } from '../store/store';
import type { ChatCitation } from '../types/chatStream';
import type { LectureTreeNode } from '../types/Lecture';
import { mcgrawHillChapters } from './McGrawHillChapterNav';
import { MCGRAW_HILL_TEXTBOOK_EXAM_IDS } from './mcgrawHill';
import { getMcGrawHillMode } from './mcgrawHillModeStore';
import { stripCiteMarkers } from './mcgrawHillCitations';
import { stripQuoteMarkers } from './mcgrawHillQuoteMarkers';
import {
  findMcGrawHillScriptedAnswer,
  type McGrawHillScriptedAnswer,
  type McGrawHillScriptedSource,
} from './mcgrawHillScriptedAnswers';

/**
 * mcgraw-hill: the AI desktop's chat transport for McGraw Hill members. A
 * scripted question asked on the bare desktop is answered here, in the
 * backend's own SSE events, so the real parser and thread render it exactly
 * like a live answer: status steps fill the thinking box, the text streams in
 * word by word, the sources land under it. Nothing reaches the server for it,
 * so it costs no chat and is not saved (it is gone after a reload). Every
 * other request goes to the real transport unchanged.
 *
 * The answer keeps the turn's draft conversation, so the thread never takes a
 * server id: the URL and history stay as they were, and a follow-up starts a
 * real conversation for itself (the draft path a first turn takes anyway).
 */

/** The request body the stream client sends (chatStreamClient.ts). */
interface TurnBody {
  message?: unknown;
  envelope?: {
    surface?: unknown;
    route?: unknown;
    anchor?: unknown;
    viewing?: unknown;
  };
}

/** How long each streamed word chunk waits after the previous one. */
const TEXT_CHUNK_MS = 28;
/** Pause between the last thinking step and the first words. */
const ANSWER_START_MS = 250;

const readBody = (init: RequestInit | undefined): TurnBody | null => {
  if (typeof init?.body !== 'string') return null;
  try {
    return JSON.parse(init.body) as TurnBody;
  } catch {
    return null;
  }
};

/**
 * The bare AI desktop: the chat page on the `chat` route with nothing open
 * beside it. A document session (anchor) or a viewed document leaves it to the
 * real AI.
 */
export const isBareDesktopTurn = (body: TurnBody): boolean => {
  const envelope = body.envelope;
  return (
    envelope?.surface === 'chat_page' &&
    envelope.route === 'chat' &&
    !envelope.anchor &&
    !envelope.viewing
  );
};

/** A turn from the AI desktop's composer, the surface the mode toggle sets. */
const isDesktopComposerTurn = (body: TurnBody): boolean =>
  body.envelope?.surface === 'chat_page';

/** The draft conversation the turn being sent belongs to. */
const sendingConversationId = (message: string): string => {
  const { chat } = store.getState() as RootState;

  const sending = Object.values(chat.turns).filter(
    (turn) => turn.status === 'requested'
  );

  const turn =
    sending.find(
      (candidate) => chat.messages[candidate.userMessageId]?.text === message
    ) ?? sending[sending.length - 1];

  return turn?.conversationId ?? `draft:${uuidv4()}`;
};

/**
 * Katzung's chapters, from the store or the server, whichever exam is active
 * (ORA-1247: a member may be studying the handbook). The server read leaves
 * the store's lecture tree, the active exam's, alone.
 */
const textbookChapterNodes = async (): Promise<LectureTreeNode[] | null> => {
  const state = store.getState() as RootState;
  const [examId] = MCGRAW_HILL_TEXTBOOK_EXAM_IDS;

  const loaded = Object.values(state.lectureTree.nodesById).filter(
    (node) => node.examId === examId
  );

  if (loaded.length > 0) return loaded;

  try {
    return (await getLectureTree(examId)).tree.nodes;
  } catch {
    return null;
  }
};

/** The script's sources as citations; a chapter not found stays a label. */
export const resolveScriptedSources = (
  sources: readonly McGrawHillScriptedSource[],
  nodes: readonly LectureTreeNode[] | null
): ChatCitation[] => {
  const examId = nodes?.[0]?.examId;
  const chapters = nodes && examId ? mcgrawHillChapters(nodes, examId) : [];

  return sources.map((source, index): ChatCitation => {
    const id = `mh-source-${index + 1}`;

    if (source.kind === 'document') {
      return {
        id,
        kind: 'chat_document_page',
        contentId: source.documentId,
        documentId: source.documentId,
        title: source.title,
        page: source.page,
        label: source.label,
        // What opening it highlights: the passage, else the heading.
        excerpt: source.excerpt ?? source.label,
        rendered: false,
        addable: false,
        target: {
          route: 'chat_document',
          params: { documentId: source.documentId, page: String(source.page) },
        },
      };
    }

    if (source.kind === 'chapter') {
      const chapter = chapters.find(
        (candidate) =>
          candidate.number !== null &&
          Number(candidate.number) === source.chapter
      );

      if (chapter) {
        return {
          id,
          kind: 'document_page',
          contentId: chapter.lectureId,
          lectureId: chapter.lectureId,
          title: chapter.name,
          page: source.page,
          label: source.label,
          ...(source.excerpt ? { excerpt: source.excerpt } : {}),
          rendered: false,
          addable: false,
        };
      }
    }

    return {
      id,
      kind: 'reference',
      contentId: '',
      label: source.label,
      rendered: false,
      addable: false,
    };
  });
};

/** The answer split into streamable chunks: words with their spacing. */
export const answerChunks = (answer: string): string[] =>
  answer.match(/\S+\s*|\s+/g) ?? [];

const sse = (event: string, data: Record<string, unknown>): string =>
  `event: ${event}\ndata: ${JSON.stringify(data)}\n\n`;

const wait = (ms: number, signal: AbortSignal | null | undefined) =>
  new Promise<void>((resolve, reject) => {
    if (signal?.aborted) {
      reject(new DOMException('Aborted', 'AbortError'));
      return;
    }

    const onAbort = () => {
      clearTimeout(timer);
      reject(new DOMException('Aborted', 'AbortError'));
    };

    // Each word waits once, so the listener goes when the wait is over.
    const timer = setTimeout(() => {
      signal?.removeEventListener('abort', onAbort);
      resolve();
    }, ms);

    signal?.addEventListener('abort', onAbort, { once: true });
  });

/** The scripted answer as a streamed SSE response. */
const scriptedResponse = (
  script: McGrawHillScriptedAnswer,
  message: string,
  signal: AbortSignal | null | undefined
): Response => {
  const turnId = `mh-script-${uuidv4()}`;
  const conversationId = sendingConversationId(message);
  const { chat } = store.getState() as RootState;
  // The day's allowance as it stands: a scripted answer spends no chat.
  const limits = chat.limits ?? undefined;
  const encoder = new TextEncoder();
  let seq = 0;
  const startedAt = Date.now();

  const body = new ReadableStream<Uint8Array>({
    async start(controller) {
      const emit = (event: string, data: Record<string, unknown>) => {
        seq += 1;

        controller.enqueue(
          encoder.encode(sse(event, { turnId, seq, ...data }))
        );
      };

      try {
        emit('turn.start', {
          protocol: 1,
          conversationId,
          model: 'ora',
          ...(limits ? { limits } : {}),
        });

        const sourcesPromise = textbookChapterNodes().then((nodes) =>
          resolveScriptedSources(script.sources, nodes)
        );

        for (const step of script.thinking) {
          emit('status', { label: step.label });
          await wait(step.ms, signal);
        }

        await wait(ANSWER_START_MS, signal);

        // Each card goes out just before the words that place it, so the
        // answer builds top to bottom: the answer's `[[quote:N]]`,
        // `[[figure:N]]` and `[[actions]]` lines become markers naming them.
        const cards = new Map<string, Record<string, unknown>>();
        const quoteBlockId = (index: number) => `${turnId}-quote-${index}`;

        (script.quotes ?? []).forEach((quote, index) => {
          cards.set(quoteBlockId(index), {
            blockId: quoteBlockId(index),
            kind: 'document_excerpt',
            placement: 'inline',
            payload: {
              source: 'chat_document',
              documentId: quote.documentId,
              page: quote.page,
              title: quote.title,
              excerpt: quote.excerpt,
            },
            target: {
              route: 'chat_document',
              params: {
                documentId: quote.documentId,
                page: String(quote.page),
              },
            },
          });
        });

        // Figure cards go out the same way, placed with the same marker.
        const figureBlockId = (index: number) => `${turnId}-figure-${index}`;

        (script.figures ?? []).forEach((figure, index) => {
          cards.set(figureBlockId(index), {
            blockId: figureBlockId(index),
            kind: 'mcgraw_hill_figure',
            placement: 'inline',
            payload: { ...figure },
          });
        });

        // The action row goes out the same way, placed by `[[actions]]`.
        const actionsBlockId = `${turnId}-actions`;
        const hasActions = Boolean(script.actions);

        if (script.actions) {
          cards.set(actionsBlockId, {
            blockId: actionsBlockId,
            kind: 'mcgraw_hill_actions',
            placement: 'inline',
            payload: {
              open: script.actions.open ?? null,
              copyText: stripCiteMarkers(
                stripQuoteMarkers(
                  script.answer.replace(/\[\[(?:actions|figure:\d+)\]\]/g, '')
                )
              )
                .replace(/\*\*/g, '')
                .replace(/\n{3,}/g, '\n\n')
                .trim(),
            },
          });
        }

        const answer = script.answer
          .replace(/\[\[actions\]\]/g, () =>
            hasActions ? `[[quote:${actionsBlockId}]]` : ''
          )
          .replace(/\[\[figure:(\d+)\]\]/g, (_marker, index: string) =>
            script.figures?.[Number(index)]
              ? `[[quote:${figureBlockId(Number(index))}]]`
              : ''
          )
          .replace(/\[\[quote:(\d+)\]\]/g, (_marker, index: string) =>
            script.quotes?.[Number(index)]
              ? `[[quote:${quoteBlockId(Number(index))}]]`
              : ''
          );

        const emitCards = (placed: (blockId: string) => boolean) => {
          for (const [blockId, card] of cards) {
            if (!placed(blockId)) continue;
            emit('block', card);
            cards.delete(blockId);
          }
        };

        for (const chunk of answerChunks(answer)) {
          emitCards((blockId) => chunk.includes(`[[quote:${blockId}]]`));
          emit('text.delta', { delta: chunk });
          await wait(TEXT_CHUNK_MS, signal);
        }

        // A card the answer never places still lands, under the answer.
        emitCards(() => true);

        emit('text.done', {
          text: answer,
          citations: await sourcesPromise,
        });

        // No messageId: nothing was saved, so nothing asks for follow-ups.
        emit('turn.done', {
          traceId: turnId,
          steps: script.thinking.length,
          ms: Date.now() - startedAt,
          ...(limits ? { limits } : {}),
        });

        controller.close();
      } catch (error) {
        controller.error(error);
      }
    },
  });

  return new Response(body, {
    status: 200,
    headers: { 'Content-Type': 'text/event-stream' },
  });
};

/**
 * The desktop's transport for a McGraw Hill member: scripted questions on the
 * bare desktop are answered locally; other bare-desktop turns go to `base`
 * flagged `mcgrawHillDesktop`; every chat-page turn carries `mcgrawHillMode`;
 * everything else goes to `base` untouched.
 */
export const createMcGrawHillScriptedStreamDeps = (
  base: ChatStreamDeps
): ChatStreamDeps => ({
  ...base,
  fetchImpl: ((input: RequestInfo | URL, init?: RequestInit) => {
    const body = readBody(init);
    const message = typeof body?.message === 'string' ? body.message : '';

    const script =
      body && isBareDesktopTurn(body)
        ? findMcGrawHillScriptedAnswer(message)
        : null;

    if (script) {
      return Promise.resolve(scriptedResponse(script, message, init?.signal));
    }

    // A live desktop turn carries the desktop flag, so the backend grounds it
    // on the McGraw Hill reference documents (the chapter viewer's chat and
    // the drawer never send it). Every turn from the desktop's composer
    // carries the mode its header's Clinical / Study toggle set (ORA-1260).
    if (body && isDesktopComposerTurn(body)) {
      return base.fetchImpl(input, {
        ...init,
        body: JSON.stringify({
          ...body,
          ...(isBareDesktopTurn(body) ? { mcgrawHillDesktop: true } : {}),
          mcgrawHillMode: getMcGrawHillMode(),
        }),
      });
    }

    return base.fetchImpl(input, init);
  }) as typeof fetch,
});
